import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Mail, Send, Users, Search, UserCheck, Filter, Paperclip,
  X, FileText, Image, Clock, CheckCircle, XCircle, Trash2
} from "lucide-react";

const SendEmailPage = () => {
  const { token, hasRole, user } = useAuth();
  const fileInputRef = useRef(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [filterRole, setFilterRole] = useState("all");
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("compose");

  useEffect(() => {
    fetchMembers();
    fetchLogs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMembers = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/users`, { headers });
      const withEmail = response.data
        .filter(m => m.email && m.email.trim() !== "" && m.vloga !== "administrator")
        .sort((a, b) => (a.priimek || "").localeCompare(b.priimek || ""));
      setMembers(withEmail);
    } catch (error) {
      toast.error("Napaka pri nalaganju članov");
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/email/logs`, { headers });
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      predsednik: "Predsednik", gospodar: "Gospodar", blagajnik: "Blagajnik",
      tajnik: "Tajnik", upravnik_lovske_koce: "Upravnik koče",
      referent_strelstvo: "Ref. strelstvo", skupinovodja: "Skupinovodja",
      pripravnik: "Pripravnik", clan: "Član"
    };
    return labels[role] || role;
  };

  const filteredMembers = members.filter(m => {
    const fullName = `${m.ime} ${m.priimek}`.toLowerCase();
    const matchesSearch = fullName.includes(searchQuery.toLowerCase()) || (m.email || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === "all" || m.vloga === filterRole;
    return matchesSearch && matchesRole;
  });

  const toggleMember = (memberId) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
    setSelectAll(false);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedMembers([]);
      setSelectAll(false);
    } else {
      setSelectedMembers(filteredMembers.map(m => m.id));
      setSelectAll(true);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalSize = [...attachments, ...files].reduce((sum, f) => sum + (f.size || 0), 0);
    if (totalSize > 25 * 1024 * 1024) {
      toast.error("Skupna velikost priponk ne sme presegati 25MB");
      return;
    }
    setAttachments(prev => [...prev, ...files]);
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getFileIcon = (name) => {
    const ext = (name || "").split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) return Image;
    return FileText;
  };

  const handleSend = async () => {
    if (!selectAll && selectedMembers.length === 0) {
      toast.error("Izberite vsaj enega prejemnika");
      return;
    }
    if (!subject.trim()) {
      toast.error("Vnesite zadevo");
      return;
    }
    if (!message.trim()) {
      toast.error("Vnesite sporočilo");
      return;
    }

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("zadeva", subject);
      formData.append("vsebina", message);
      formData.append("prejemniki", selectAll ? JSON.stringify("all") : JSON.stringify(selectedMembers));
      attachments.forEach(file => {
        formData.append("priponke", file);
      });

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data"
      };
      const res = await axios.post(`${API}/email/send`, formData, { headers });
      toast.success(res.data.message);

      setSubject("");
      setMessage("");
      setAttachments([]);
      setSelectedMembers([]);
      setSelectAll(false);
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri pošiljanju emaila");
    } finally {
      setSending(false);
    }
  };

  const ldNaziv = user?.ld_naziv || "LD";
  const templates = [
    { label: "Dogodek", zadeva: `${ldNaziv} - Vabilo na dogodek`, vsebina: `Spoštovani člani,\n\nvabljeni ste na dogodek ${ldNaziv}.\n\nDatum: \nUra: \nLokacija: \n\nProsimo za potrditev udeležbe na portalu.\n\nLovski pozdrav,\n${ldNaziv}` },
    { label: "Delovna akcija", zadeva: `${ldNaziv} - Delovna akcija`, vsebina: `Spoštovani člani,\n\nobveščamo vas o delovni akciji.\n\nDatum: \nZbirno mesto: \nUra zbiranja: \n\nProsimo za udeležbo.\n\nLovski pozdrav,\n${ldNaziv}` },
    { label: "Sestanek", zadeva: `${ldNaziv} - Sestanek`, vsebina: `Spoštovani člani,\n\nvabljeni ste na sestanek ${ldNaziv}.\n\nDatum: \nUra: \nLokacija: \n\nDnevni red:\n1. \n2. \n3. \n\nLovski pozdrav,\n${ldNaziv}` },
    { label: "Obvestilo", zadeva: `${ldNaziv} - Obvestilo`, vsebina: `Spoštovani člani,\n\nobveščamo vas o naslednjem:\n\n\n\nLovski pozdrav,\n${ldNaziv}` }
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  const roles = [
    { value: "all", label: "Vsi" },
    { value: "predsednik", label: "Predsednik" },
    { value: "gospodar", label: "Gospodar" },
    { value: "tajnik", label: "Tajnik" },
    { value: "blagajnik", label: "Blagajnik" },
    { value: "skupinovodja", label: "Skupinovodja" },
    { value: "pripravnik", label: "Pripravnik" },
    { value: "clan", label: "Člani" }
  ];

  return (
    <div className="space-y-4 animate-fade-in" data-testid="send-email-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-2">
          <Mail className="h-6 w-6 sm:h-8 sm:w-8" />
          Pošlji e-mail
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pošljite e-mail s priponkami izbranim članom
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="compose" data-testid="tab-compose">
            <Send className="h-4 w-4 mr-1" /> Novo sporočilo
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Clock className="h-4 w-4 mr-1" /> Zgodovina ({logs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="compose">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Recipients - 2 columns */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Prejemniki
                  </CardTitle>
                  <Badge variant="secondary">{selectAll ? members.length : selectedMembers.length}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Išči po imenu ali emailu..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                      data-testid="search-members-input"
                    />
                  </div>
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    className="px-2 py-2 border rounded-md text-sm bg-background"
                    data-testid="filter-role-select"
                  >
                    {roles.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                {/* Select all toggle */}
                <div
                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer border ${selectAll ? "bg-primary/10 border-primary/30" : "hover:bg-muted border-transparent"}`}
                  onClick={handleSelectAll}
                  data-testid="select-all-toggle"
                >
                  <Checkbox checked={selectAll} onCheckedChange={handleSelectAll} />
                  <span className="font-medium text-sm">Izberi vse člane z e-mailom ({members.length})</span>
                </div>

                <ScrollArea className="h-[320px] border rounded-lg">
                  <div className="p-2 space-y-1">
                    {filteredMembers.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">Ni članov z e-mail naslovom</p>
                    ) : (
                      filteredMembers.map((member) => (
                        <div
                          key={member.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            selectedMembers.includes(member.id) || selectAll
                              ? "bg-primary/10 border border-primary/30"
                              : "hover:bg-muted border border-transparent"
                          }`}
                          onClick={() => toggleMember(member.id)}
                          data-testid={`member-${member.id}`}
                        >
                          <Checkbox checked={selectedMembers.includes(member.id) || selectAll} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{member.priimek} {member.ime}</p>
                            <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                              <Mail className="h-3 w-3 flex-shrink-0" />
                              {member.email}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-[10px] flex-shrink-0">{getRoleLabel(member.vloga)}</Badge>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
                <p className="text-xs text-muted-foreground">{filteredMembers.length} članov z e-mail naslovom</p>
              </CardContent>
            </Card>

            {/* Compose - 3 columns */}
            <Card className="lg:col-span-3">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  Sporočilo
                </CardTitle>
                <CardDescription>Sestavite e-mail sporočilo s priponkami</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Templates */}
                <div>
                  <Label className="text-xs text-muted-foreground">Predloge:</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {templates.map((t) => (
                      <Button
                        key={t.label}
                        size="sm"
                        variant="outline"
                        onClick={() => { setSubject(t.zadeva); setMessage(t.vsebina); }}
                        data-testid={`template-${t.label}`}
                      >
                        {t.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <Label htmlFor="subject">Zadeva *</Label>
                  <Input
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Zadeva e-maila..."
                    className="mt-1"
                    data-testid="email-subject-input"
                  />
                </div>

                {/* Message body */}
                <div>
                  <Label htmlFor="body">Vsebina *</Label>
                  <Textarea
                    id="body"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Napišite sporočilo..."
                    rows={8}
                    className="mt-1 resize-none"
                    data-testid="email-body-textarea"
                  />
                </div>

                {/* Attachments */}
                <div>
                  <Label className="flex items-center gap-1">
                    <Paperclip className="h-4 w-4" />
                    Priponke
                  </Label>
                  <div className="mt-2 space-y-2">
                    {attachments.length > 0 && (
                      <div className="space-y-1">
                        {attachments.map((file, i) => {
                          const FileIcon = getFileIcon(file.name);
                          return (
                            <div key={file.name + file.size} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg text-sm">
                              <FileIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span className="flex-1 truncate">{file.name}</span>
                              <span className="text-xs text-muted-foreground flex-shrink-0">{formatFileSize(file.size)}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
                                onClick={() => removeAttachment(i)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          );
                        })}
                        <p className="text-xs text-muted-foreground">
                          Skupaj: {formatFileSize(attachments.reduce((sum, f) => sum + f.size, 0))}
                        </p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="add-attachment-btn"
                    >
                      <Paperclip className="h-4 w-4 mr-1" />
                      Dodaj priponko
                    </Button>
                  </div>
                </div>

                {/* Summary & Send */}
                {(selectedMembers.length > 0 || selectAll) && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-green-600" />
                      {selectAll ? members.length : selectedMembers.length} prejemnikov
                      {attachments.length > 0 && (
                        <span className="text-muted-foreground">| {attachments.length} priponk</span>
                      )}
                    </p>
                  </div>
                )}

                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleSend}
                  disabled={sending || (!selectAll && selectedMembers.length === 0) || !subject.trim() || !message.trim()}
                  data-testid="send-email-btn"
                >
                  {sending ? (
                    <>
                      <div className="spinner h-4 w-4 mr-2" /> Pošiljam...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Pošlji e-mail
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Zgodovina poslanih e-mailov
              </CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Ni poslanih e-mailov</p>
              ) : (
                <div className="space-y-3">
                  {logs.map((log) => (
                    <div key={log.id} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-sm truncate">{log.zadeva}</h4>
                            {log.status === "sent" ? (
                              <Badge variant="default" className="bg-green-600 text-[10px]">
                                <CheckCircle className="h-3 w-3 mr-0.5" /> Poslano
                              </Badge>
                            ) : (
                              <Badge variant="destructive" className="text-[10px]">
                                <XCircle className="h-3 w-3 mr-0.5" /> Napaka
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{log.vsebina}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {log.prejemniki_count} prejemnikov
                            </span>
                            {log.priponke && log.priponke.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Paperclip className="h-3 w-3" />
                                {log.priponke.length} priponk
                              </span>
                            )}
                            <span>{log.poslal_ime}</span>
                            <span>{new Date(log.datum).toLocaleString("sl-SI", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SendEmailPage;
